// Cart validation functionality
let cartValidationState = {
  isValid: true,
  hasAuthError: false,
  hasAddonError: false,
  isValidating: false,
  lastValidationTime: 0,
};

// Module-level cache for validation results
let validationCache = null;
let hasValidated = false;

// Track if Liquid disabled the checkout button at page load (country block, duplicates, etc.)
// JS should never re-enable a button that Liquid disabled
let liquidDisabledCheckout = false;

// Add-on access gate for Smart Money: Stocks & ETFs (blocks cart/checkout UI)
const SMSTOCK_ADDON_REQUIRED_TAG = "VS-ADDON-PURCHASE-SMSTOCK";
const SMSTOCK_ADDON_PRODUCT_ID = "7799746363475";
const SMSTOCK_ADDON_BLOCK_MESSAGE = `Please contact customer service for purchase access at <a href="mailto:support@verifiedinvesting.com">support@verifiedinvesting.com</a>. You must have an active subscription to <a href="/products/smart-money-stocks-etfs"><strong>Smart Money: Stocks & ETFs</strong></a> to be eligible for access.`;

// Extension product validation helpers
function getCustomerTagsForCart() {
  const tagsInput = document.getElementById("vsubJS-customerTags");
  if (!tagsInput || !tagsInput.value) return [];
  return tagsInput.value
    .split(",")
    .map((tag) => tag.trim().toLowerCase())
    .filter(Boolean);
}

function isCustomerAuthenticatedForCart() {
  const customerIdInput = document.getElementById("vsubJS-customerId");
  if (
    customerIdInput &&
    customerIdInput.value &&
    customerIdInput.value.trim() !== "" &&
    customerIdInput.value !== "0"
  ) {
    return true;
  }
  const tagsInput = document.getElementById("vsubJS-customerTags");
  return tagsInput && tagsInput.value && tagsInput.value.trim() !== "";
}

function hasSmStockAddonAccessForCart() {
  return getCustomerTagsForCart().includes(
    SMSTOCK_ADDON_REQUIRED_TAG.toLowerCase(),
  );
}

function showSmStockAddonWarningInCart(variantId, message) {
  const warningContainer = document.querySelector(
    `.cart-item-validation-warning[data-variant-id="${variantId}"]`,
  );
  if (!warningContainer) return;

  const cartItem = warningContainer.closest(".cartItem");
  if (
    cartItem &&
    cartItem.querySelector('[data-smstock-addon-warning="true"]')
  ) {
    return;
  }

  warningContainer.innerHTML = `<div class="grid-12 mt-1 warning vsubs-warning item-check">${message}</div>`;
  warningContainer.setAttribute("data-smstock-addon-warning", "true");
}

function clearSmStockAddonWarningInCart(variantId) {
  const warningContainer = document.querySelector(
    `.cart-item-validation-warning[data-variant-id="${variantId}"]`,
  );
  if (
    warningContainer &&
    warningContainer.getAttribute("data-smstock-addon-warning") === "true"
  ) {
    warningContainer.innerHTML = "";
    warningContainer.removeAttribute("data-smstock-addon-warning");
  }
}

function clearSmStockAddonWarningsInCart() {
  const warningContainers = document.querySelectorAll(
    ".cart-item-validation-warning[data-smstock-addon-warning='true']",
  );
  warningContainers.forEach((container) => {
    container.innerHTML = "";
    container.removeAttribute("data-smstock-addon-warning");
  });
}

function validateSmStockAddonInCart() {
  const cartItems = document.querySelectorAll(
    ".cartItem[data-parent-product-id]",
  );
  let hasBlockedItems = false;
  const hasTag = hasSmStockAddonAccessForCart();

  cartItems.forEach((cartItem) => {
    if (isRemovedCartItem(cartItem)) {
      return;
    }

    const productId = cartItem.getAttribute("data-parent-product-id");
    if (String(productId) !== SMSTOCK_ADDON_PRODUCT_ID) {
      return;
    }

    if (hasTag) {
      clearSmStockAddonWarningInCart(cartItem.getAttribute("data-variant-id"));
      return;
    }

    hasBlockedItems = true;
    const hasLiquidWarning = cartItem.querySelector(
      '[data-smstock-addon-warning="true"]',
    );
    if (!hasLiquidWarning) {
      showSmStockAddonWarningInCart(
        cartItem.getAttribute("data-variant-id"),
        SMSTOCK_ADDON_BLOCK_MESSAGE,
      );
    }
  });

  return { hasBlockedItems };
}

function parseExtensionSettings(settingsJson) {
  if (!settingsJson) return null;
  try {
    const unescaped =
      typeof unescapeHtmlAttribute === "function"
        ? unescapeHtmlAttribute(settingsJson)
        : String(settingsJson)
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, "&")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&#39;/g, "'");

    return JSON.parse(unescaped);
  } catch (e) {
    console.error("Failed to parse extension settings:", e);
    return null;
  }
}

function validateExtensionProductAccessForCart(settings) {
  if (!settings) return { allowed: true, reason: null };

  const parentPlanTags = settings.parent_plan_tags || "";
  const extensionOpenPeriodTags = settings.extension_open_period_key_tags || "";

  const requiredParentTags = parentPlanTags
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  const requiredExtensionTags = extensionOpenPeriodTags
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);

  if (requiredParentTags.length === 0 && requiredExtensionTags.length === 0) {
    return { allowed: true, reason: null };
  }

  if (!isCustomerAuthenticatedForCart()) {
    return {
      allowed: false,
      reason: "NOT_AUTHENTICATED",
      parentPlanTitle: settings.parent_plan_product_title,
    };
  }

  const customerTags = getCustomerTagsForCart();

  const hasAllParentTags =
    requiredParentTags.length > 0 &&
    requiredParentTags.every((tag) => customerTags.includes(tag));

  const hasAllExtensionTags =
    requiredExtensionTags.length > 0 &&
    requiredExtensionTags.every((tag) => customerTags.includes(tag));

  if (hasAllParentTags || hasAllExtensionTags) {
    return { allowed: true, reason: null };
  }

  return {
    allowed: false,
    reason: "MISSING_TAGS",
    parentPlanTitle: settings.parent_plan_product_title,
    requiredParentTags,
    requiredExtensionTags,
  };
}

function showExtensionValidationWarning(cartItem, result, settings) {
  const variantId = cartItem.getAttribute("data-variant-id");
  const warningContainer = document.querySelector(
    `.cart-item-validation-warning[data-variant-id="${variantId}"]`,
  );
  if (!warningContainer) return;

  const parentPlanTitle =
    settings.parent_plan_product_title || "the required plan";
  let message = "";

  if (result.reason === "NOT_AUTHENTICATED") {
    message = `This is an extension product. You must be logged in with an active subscription to <strong>${parentPlanTitle}</strong> to purchase this item.`;
  } else {
    message = `This extension product requires an active subscription to <strong>${parentPlanTitle}</strong>. Please ensure you have the required subscription before purchasing.`;
  }

  warningContainer.innerHTML = `<div class="grid-12 mt-1 warning vsubs-warning item-check">${message}</div>`;
}

function validateExtensionProductsInCart() {
  const cartItems = document.querySelectorAll(
    ".cartItem[data-extension-settings]",
  );
  let hasBlockedItems = false;

  cartItems.forEach((cartItem) => {
    const settingsJson = cartItem.getAttribute("data-extension-settings");
    const settings = parseExtensionSettings(settingsJson);

    if (settings) {
      const result = validateExtensionProductAccessForCart(settings);
      if (!result.allowed) {
        hasBlockedItems = true;
        showExtensionValidationWarning(cartItem, result, settings);
      }
    }
  });

  return !hasBlockedItems;
}

// ========== Add-on Product Validation in Cart ==========

function parseAddonSettings(settingsJson) {
  if (!settingsJson) return null;
  try {
    const unescaped =
      typeof unescapeHtmlAttribute === "function"
        ? unescapeHtmlAttribute(settingsJson)
        : String(settingsJson)
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, "&")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&#39;/g, "'");

    return JSON.parse(unescaped);
  } catch (e) {
    console.error("Failed to parse addon settings:", e);
    return null;
  }
}

function formatShortDateForCart(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function buildAddonLockedMessageForCart(parentTitle, parentUrl) {
  const titleHtml = parentUrl
    ? `<a href="${parentUrl}"><strong>${parentTitle}</strong></a>`
    : `<strong>${parentTitle}</strong>`;
  return `This add-on is exclusively available as part of an active ${titleHtml} subscription and cannot be purchased separately.`;
}

function buildAddonAlignmentMessageForCart(parentTitle, nextBillingDate) {
  // const formattedDate = formatShortDateForCart(nextBillingDate);
  return `You're all set! You now have full access to your add-on. It will renew automatically with your next <strong>${parentTitle}</strong> subscription bill, and all future charges will be combined into a single easy payment.`;
}

function shouldSuppressAddonReadyMessageForCart(variantId) {
  const cartItem = document.querySelector(
    `.cartItem[data-variant-id="${variantId}"]`,
  );
  if (!cartItem) {
    return false;
  }

  const productId = cartItem.getAttribute("data-parent-product-id");
  if (String(productId) !== SMSTOCK_ADDON_PRODUCT_ID) {
    return false;
  }

  return Boolean(cartItem.querySelector('[data-smstock-addon-warning="true"]'));
}

function buildAddonReadyToPurchaseMessageForCart(
  parentTitle,
  nextBillingDate,
  variantId,
) {
  // Theme-specific override: suppress ready-to-purchase message when the
  // Smart Money add-on access gate is already shown in the cart.
  if (variantId && shouldSuppressAddonReadyMessageForCart(variantId)) {
    return null;
  }

  // const formattedDate = formatShortDateForCart(nextBillingDate);

  return `Ready to add this feature? You'll be charged today and gain access immediately. Your add-on will then automatically renew alongside your <strong>${parentTitle}</strong> subscription moving forward.`;
}

function getAddonCartItems() {
  const items = document.querySelectorAll(
    ".cartItem[data-addon-product='true']",
  );
  return items;
}

// Blocking and informational ("Ready to add this feature?") add-on messages
// share one box, as on the product page. The informational one used to render
// unboxed; whether a line blocks checkout is tracked in hasBlockedItems, not
// by the markup.
function showAddonWarningInCart(variantId, message) {
  const warningContainer = document.querySelector(
    `.cart-item-validation-warning[data-variant-id="${variantId}"]`,
  );
  if (!warningContainer) return;

  warningContainer.innerHTML = `<div class="grid-12 mt-1 warning vsubs-warning item-check">${message}</div>`;
}

function clearAddonWarningInCart(variantId) {
  const warningContainer = document.querySelector(
    `.cart-item-validation-warning[data-variant-id="${variantId}"]`,
  );
  if (warningContainer) {
    warningContainer.innerHTML = "";
  }
}

function isRemovedCartItem(element) {
  return (
    element?.classList?.contains("reconsider") ||
    element?.closest?.(".reconsider")
  );
}

async function getCartVariantIdsFromAjax() {
  const root = window?.Shopify?.routes?.root || "/";
  const response = await fetch(`${root}cart.js`, {
    method: "GET",
    headers: {
      "Cache-Control": "no-cache, no-store, must-revalidate",
      Pragma: "no-cache",
      Expires: "0",
    },
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(`Cart fetch failed: ${response.status}`);
  }

  const data = await response.json();
  const items = Array.isArray(data?.items) ? data.items : [];
  return items
    .map((item) => String(item?.variant_id || "").trim())
    .filter(Boolean);
}

async function validateAddonsInCart() {
  const addonItems = getAddonCartItems();
  if (addonItems.length === 0) {
    return { hasBlockedItems: false };
  }

  if (!isCustomerAuthenticatedForCart()) {
    let hasBlockedItems = false;
    addonItems.forEach((cartItem) => {
      const variantId = cartItem.getAttribute("data-variant-id");
      const settingsJson = cartItem.getAttribute("data-addon-settings");
      const settings = parseAddonSettings(settingsJson);

      if (settings && settings.enabled) {
        hasBlockedItems = true;
        const message = `<strong>Login required.</strong><br>You must be logged in to purchase this add-on.`;
        showAddonWarningInCart(variantId, message);
      }
    });

    return { hasBlockedItems };
  }

  try {
    const authData = await getRequired();
    if (!authData || !authData.token) {
      return { hasBlockedItems: false };
    }

    const addonVariantIds = [];
    addonItems.forEach((cartItem) => {
      const variantId = cartItem.getAttribute("data-variant-id");
      const settingsJson = cartItem.getAttribute("data-addon-settings");
      const settings = parseAddonSettings(settingsJson);

      if (settings && settings.enabled && variantId) {
        addonVariantIds.push(variantId);
      }
    });

    if (addonVariantIds.length === 0) {
      return { hasBlockedItems: false };
    }

    let allVariantIds = [];
    try {
      allVariantIds = await getCartVariantIdsFromAjax();
    } catch (error) {
      console.error("[vsub][cart-validation] cart.js failed", error);
      const allCartItems = document.querySelectorAll(
        ".cartItem[data-variant-id]",
      );
      allCartItems.forEach((item) => {
        const variantId = item.getAttribute("data-variant-id");
        const removed = isRemovedCartItem(item);

        if (removed) {
          return;
        }
        if (variantId) {
          allVariantIds.push(variantId);
        }
      });
    }

    const response = await fetch("/apps/app-proxy/api/addons/validate-cart", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authData.token}`,
        shop_name: authData.shopDomain,
        "Content-Type": "application/json",
        "Cache-Control": "no-cache, no-store, must-revalidate",
      },
      body: JSON.stringify({
        customer_id: authData.customerId,
        variant_ids: allVariantIds,
        is_cart: true,
      }),
      cache: "no-store",
    });

    if (!response.ok) {
      return { hasBlockedItems: false };
    }

    const data = await response.json();
    let hasBlockedItems = false;

    if (data.validationResults && data.validationResults.length > 0) {
      data.validationResults.forEach((result) => {
        const variantId = result.variant_id;
        const cartItem = document.querySelector(
          `.cartItem[data-variant-id="${variantId}"]`,
        );

        if (!cartItem) {
          return;
        }

        const settingsJson = cartItem.getAttribute("data-addon-settings");
        const settings = parseAddonSettings(settingsJson);
        const parentTitle =
          settings?.parent_plan_product_title || "the parent subscription";
        const parentUrl = settings?.parent_plan_product_url;

        if (result.can_add_to_cart) {
          const nextBillingDate = result.parent_subscription_next_billing_date;
          if (nextBillingDate) {
            const message = buildAddonReadyToPurchaseMessageForCart(
              parentTitle,
              nextBillingDate,
              variantId,
            );
            if (message) {
              showAddonWarningInCart(variantId, message);
            } else {
              clearAddonWarningInCart(variantId);
            }
          } else {
            clearAddonWarningInCart(variantId);
          }
        } else {
          hasBlockedItems = true;
          if (result.reason === "ADDON_ALREADY_ACTIVE") {
            const nextBillingDate =
              result.parent_subscription_next_billing_date;
            const message = buildAddonAlignmentMessageForCart(
              parentTitle,
              nextBillingDate,
            );
            showAddonWarningInCart(variantId, message);
          } else if (result.reason === "MISSING_PARENT_SUBSCRIPTION") {
            const message = buildAddonLockedMessageForCart(
              parentTitle,
              parentUrl,
            );
            showAddonWarningInCart(variantId, message);
          } else if (result.reason === "ADDON_PRODUCT_FAMILY_CONFLICT") {
            const activeVariantTitle =
              result.active_addon_variant_title || "another variant";
            const message = `You already have an active <strong>${activeVariantTitle}</strong> add-on in this product family. Only one add-on per product family is allowed.`;
            showAddonWarningInCart(variantId, message);
          } else {
            const message =
              result.message || "Unable to add this add-on to cart.";
            showAddonWarningInCart(variantId, message);
          }
        }
      });
    }

    cartValidationState.hasAddonError = hasBlockedItems;

    return { hasBlockedItems };
  } catch (error) {
    console.error("Add-on cart validation error:", error);
    return { hasBlockedItems: false };
  }
}

// ========== End Add-on Product Validation in Cart ==========

/**
 * Runs one cart validation pass.
 *
 * Returns whether the pass reached a verdict for the cart. The inline
 * plan-switch cancel needs that answer: a cancel that succeeds but cannot be
 * re-validated leaves a stale banner above a dead button, and the customer has
 * to be told to refresh. Every early return that gives up on the API results
 * also clears this function's own banners first — otherwise the stale one it
 * could not refresh stays on screen.
 */
async function validateCartItems() {
  // Return early if we've already validated this page load
  if (hasValidated) {
    return false;
  }

  // Prevent infinite loops and rapid re-validation
  const now = Date.now();
  if (
    cartValidationState.isValidating ||
    now - cartValidationState.lastValidationTime < 100
  ) {
    return false;
  }

  cartValidationState.isValidating = true;
  cartValidationState.lastValidationTime = now;

  try {
    cartValidationState.isValid = true;

    const smstockAddonValidationResult = validateSmStockAddonInCart();
    if (smstockAddonValidationResult.hasBlockedItems) {
      cartValidationState.isValid = false;
      updateCheckoutButton(false);
      hasValidated = true;
      // Checkout is settled by the Smart Money gate, so no API verdict will be
      // fetched to replace whatever this function left behind last time.
      clearValidationWarnings();
      return true;
    }

    // First, validate extension products (client-side, based on customer tags)
    const extensionProductsValid = validateExtensionProductsInCart();
    if (!extensionProductsValid) {
      cartValidationState.isValid = false;

      updateCheckoutButton(false);
    }

    // Validate add-on products (requires API call)
    const addonValidationResult = await validateAddonsInCart();
    if (addonValidationResult.hasBlockedItems) {
      cartValidationState.isValid = false;

      updateCheckoutButton(false);
    }

    // Check if customer is authenticated by trying to get required data
    let authData;
    try {
      authData = await getRequired();
    } catch (error) {
      hasValidated = true;
      clearValidationWarnings();
      return false;
    }

    if (!authData || !authData.token || !authData.customerId) {
      hasValidated = true;
      clearValidationWarnings();
      return false;
    }

    // Clear previous authentication warnings
    clearAuthenticationWarning();

    // Collect variant IDs from cart items
    const variantIds = [];
    const cartItems = document.querySelectorAll(".cartItem[data-variant-id]");

    cartItems.forEach((item) => {
      if (isRemovedCartItem(item)) {
        return;
      }
      const variantId = item.getAttribute("data-variant-id");
      if (variantId) {
        variantIds.push(variantId);
      }
    });

    if (variantIds.length === 0) {
      updateCheckoutButton(!cartValidationState.hasAddonError);
      hasValidated = true;
      validationCache = [];
      // Nothing left to judge, so nothing this function wrote can still apply.
      clearValidationWarnings();
      return true;
    }

    // Call validation API
    const response = await fetch(
      "/apps/app-proxy/api/subscriptions/variant-validations",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authData.token}`,
          shop_name: authData.shopDomain,
          "Content-Type": "application/json",
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
        body: JSON.stringify({
          customer_id: authData.customerId,
          variant_ids: variantIds,
          // The legacy endpoint always assumed cart context; this one needs to
          // be told, or the same-product-twice conflict would stop firing here.
          is_cart: true,
        }),
        cache: "no-store",
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const validationData = await response.json();

    // Store results in cache and mark as validated
    validationCache = validationData.validationResults;
    hasValidated = true;

    processValidationResults(validationCache);
    return true;
  } catch (error) {
    console.error("Cart validation error:", error);
    // The verdicts on screen could not be refreshed and may already be wrong —
    // a line whose pending switch was just cancelled, for instance.
    clearValidationWarnings();
    return false;
  } finally {
    cartValidationState.isValidating = false;
  }
}

function processValidationResults(results) {
  let hasBlockedItems = false;

  // Clear previous validation warnings for API results only (not extension/addon warnings)
  // Extension and addon warnings are managed by their respective validation functions.
  // Without this a line that has become valid — after cancelling the pending
  // switch inline, for instance — keeps its stale banner and its dead button.
  clearValidationWarnings();

  // Re-validate extension products to ensure their warnings persist
  const extensionProductsValid = validateExtensionProductsInCart();
  if (!extensionProductsValid) {
    hasBlockedItems = true;
  }

  // Then process API validation results
  results.forEach((result) => {
    // Show warnings for items that can't be added to cart OR have EOL dates (informational)
    if (!result.can_add_to_cart || result.plan_end_of_life_date) {
      // PRODUCT_CONFLICT stays exempt: it is handled by the metafield approach
      // in cart-checkout-button.liquid, so blocking it here would duplicate an
      // existing gate. PENDING_SWITCH now blocks, because checking out would
      // create a second subscription in a product family the customer is
      // already moving into. EOL never blocks — it is informational.
      if (!result.can_add_to_cart && result.reason !== "PRODUCT_CONFLICT") {
        hasBlockedItems = true;
      }
      showValidationWarning(result);
    }
  });

  cartValidationState.isValid =
    !hasBlockedItems && !cartValidationState.hasAddonError;

  updateCheckoutButton(!hasBlockedItems && !cartValidationState.hasAddonError);
}

// Process cart changes using cached validation results
function processCartChange() {
  // If we haven't validated yet or don't have cached results, do nothing
  if (!hasValidated || !validationCache) {
    return;
  }

  // Get current cart items from DOM
  const currentCartItems = document.querySelectorAll(
    ".cartItem[data-variant-id]",
  );
  const currentVariantIds = new Set();

  currentCartItems.forEach((item) => {
    if (isRemovedCartItem(item)) {
      return;
    }
    const variantId = item.getAttribute("data-variant-id");
    if (variantId) {
      currentVariantIds.add(variantId);
    }
  });

  // Filter cached results to only include variants still in cart
  const filteredCache = validationCache.filter((result) =>
    currentVariantIds.has(result.variant_id.toString()),
  );

  // Update the cache with filtered results
  validationCache = filteredCache;

  // Process the filtered results to update UI
  processValidationResults(validationCache);
}

// End of Life copy — informational, and shown only when nothing is blocking the
// line, so a blocked line never explains itself with the wrong notice.
function buildEolMessage(result) {
  // Server data going into innerHTML; escaped with the shared helper from
  // app-vsub.js.
  const planTitle = escapeHtmlAttribute(
    String(result.plan_title || "This plan"),
  );
  const eolDate = new Date(result.plan_end_of_life_date).toLocaleDateString(
    "en-US",
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    },
  );

  return `<strong>${planTitle}</strong> will be
                    discontinued on <strong>${eolDate}</strong>. No charges will
                    occur after this date. Any existing subscriptions will be
                    canceled.`;
}

function showValidationWarning(result) {
  const warningContainer = document.querySelector(
    `.cart-item-validation-warning[data-variant-id="${result.variant_id}"]`,
  );
  if (!warningContainer) return;

  let message = "";

  // The reason a line is blocked outranks the End of Life notice, which is only
  // informational — checkout is disabled either way, and the customer needs to
  // read the reason it is disabled. This matches the product page.
  // PRODUCT_CONFLICT is the exception: cart-checkout-button.liquid already
  // explains that one, so the line falls through to the End of Life notice
  // rather than saying nothing at all.
  const blocking =
    !result.can_add_to_cart && result.reason !== "PRODUCT_CONFLICT";

  if (blocking) {
    switch (result.reason) {
      case "PRODUCT_FAMILY_CONFLICT":
        if (result.conflicting_subscription) {
          message = `You already have an active subscription for product "${escapeHtmlAttribute(
            String(result.conflicting_subscription.product_title || ""),
          )}". This prevents adding similar products to your cart.`;
        } else {
          message = "This product conflicts with your existing subscriptions.";
        }
        break;

      case "PRODUCT_FAMILY_CONFLICT_NON_EXISTING_SUB":
        message =
          "You cannot add multiple recurring subscriptions of the same product to your cart. Please choose only one recurring subscription per product and remove the others to continue.";
        break;

      case "PENDING_SWITCH":
        // Shared with the product page; see vsubPendingSwitchMessage in
        // app-vsub.js. Renders the reactivation preview and either the inline
        // cancel button or an explanation of why it is not offered.
        message = vsubPendingSwitchMessage(result);
        break;

      default:
        message =
          "This product cannot be added to your cart due to subscription restrictions.";
    }
  } else if (result.plan_end_of_life_date) {
    message = buildEolMessage(result);
  }

  if (!message) return;

  warningContainer.innerHTML = `<div class="grid-12 mt-1 warning vsubs-warning item-check">${message}</div>`;
  // Extension, add-on and Smart Money warnings share these containers, so mark
  // the ones this function wrote — they are the only ones it may clear.
  warningContainer.setAttribute("data-vsub-validation-warning", "true");
}

// Clears only the warnings written from the API validation results. Extension,
// add-on and Smart Money warnings live in the same containers but are owned by
// their own functions, so they are left untouched.
function clearValidationWarnings() {
  const warningContainers = document.querySelectorAll(
    ".cart-item-validation-warning[data-vsub-validation-warning='true']",
  );
  warningContainers.forEach((container) => {
    container.innerHTML = "";
    container.removeAttribute("data-vsub-validation-warning");
  });
}

function clearAuthenticationWarning() {
  cartValidationState.hasAuthError = false;
  const warningsContainer = document.getElementById("cart-validation-warnings");
  if (warningsContainer) {
    warningsContainer.innerHTML = "";
  }
}

function updateCheckoutButton(enabled) {
  // Never re-enable if Liquid disabled the button at page load
  if (enabled && liquidDisabledCheckout) {
    return;
  }

  const checkoutContainer = document.getElementById(
    "checkout-button-container",
  );
  if (!checkoutContainer) return;

  const checkoutButton = checkoutContainer.querySelector(
    'button[type="submit"], input[type="submit"], .btn',
  );

  if (checkoutButton) {
    checkoutButton.disabled = !enabled;

    if (!enabled) {
      checkoutButton.style.opacity = "0.5";
      checkoutButton.style.cursor = "not-allowed";

      // Add click prevention
      checkoutButton.addEventListener("click", function (e) {
        if (!cartValidationState.isValid || cartValidationState.hasAuthError) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      });
    } else {
      checkoutButton.style.opacity = "1";
      checkoutButton.style.cursor = "pointer";
    }
  }
}

// Direct validation function - always forces full re-validation
// Cart changes (item removed, quantity updated) require fresh validation
// since addon validation results are not cached
function triggerValidation() {
  hasValidated = false;
  cartValidationState.hasAddonError = false;
  clearAllAddonWarnings();
  clearSmStockAddonWarningsInCart();
  validateCartItems();
}

function clearAllAddonWarnings() {
  const addonItems = getAddonCartItems();
  addonItems.forEach((cartItem) => {
    const variantId = cartItem.getAttribute("data-variant-id");
    if (variantId) {
      clearAddonWarningInCart(variantId);
    }
  });
}

// Initialize cart validation when DOM is ready
function initCartValidation() {
  // Capture if Liquid disabled the checkout button before JS runs
  const checkoutContainer = document.getElementById(
    "checkout-button-container",
  );
  if (checkoutContainer) {
    const checkoutButton = checkoutContainer.querySelector(
      'button[type="submit"], input[type="submit"], .btn',
    );
    if (checkoutButton && checkoutButton.disabled) {
      liquidDisabledCheckout = true;
    }
  }

  // Run validation on page load with delay to ensure DOM is fully settled
  document.addEventListener("DOMContentLoaded", function () {
    if (document.querySelector(".cartItem")) {
      setTimeout(() => {
        validateCartItems();
      }, 500);
    }
  });

  // A successful cancel re-reads from the server rather than optimistically
  // re-enabling checkout: the restored subscription may itself block the line.
  // All three guards have to be reset or validateCartItems returns immediately —
  // the rate limiter included, since a fast cancel can land inside its window
  // and would otherwise be reported as a failed re-read.
  vsubBindPendingSwitchCancel(async function () {
    hasValidated = false;
    validationCache = null;
    cartValidationState.isValidating = false;
    cartValidationState.lastValidationTime = 0;
    // validateCartItems handles its own errors, so a re-read that did not
    // complete is thrown here to reach the shared "Refresh to continue"
    // fallback instead of being reported as success.
    const revalidated = await validateCartItems();
    if (!revalidated) {
      throw new Error("Cart validation did not complete after cancel");
    }
  });

  // Run validation when cart is updated
  document.addEventListener("click", function (e) {
    if (
      e.target.classList.contains("jsItemUpdate") ||
      e.target.classList.contains("jsItemRemove") ||
      e.target.closest(".cart-item-remove") ||
      e.target.closest("[data-line]") ||
      e.target.href?.includes("/cart/change") ||
      e.target.closest("a[href*='/cart/change']")
    ) {
      // Add delay for both update and remove actions to allow cart to update first
      if (e.target.classList.contains("jsItemUpdate")) {
        setTimeout(() => {
          triggerValidation();
        }, 500);
      } else if (e.target.classList.contains("jsItemRemove")) {
        setTimeout(() => {
          triggerValidation();
        }, 500);
      } else {
        // For other cart actions, trigger immediately
        triggerValidation();
      }
    }
  });

  // Also run validation when quantities change
  document.addEventListener("change", function (e) {
    if (
      e.target.classList.contains("jsItemQty") ||
      e.target.name?.includes("updates") ||
      (e.target.type === "number" && e.target.closest(".cartItem"))
    ) {
      // Trigger validation for quantity changes
      triggerValidation();
    }
  });

  // Listen for form submissions (cart updates)
  document.addEventListener("submit", function (e) {
    if (e.target.action?.includes("/cart") || e.target.id === "cart") {
      triggerValidation();
    }
  });

  // Listen for custom cart events
  document.addEventListener("cart:updated", function () {
    if (hasValidated) {
      processCartChange();
    } else {
      validateCartItems();
    }
  });

  document.addEventListener("cart:changed", function () {
    if (hasValidated) {
      processCartChange();
    } else {
      validateCartItems();
    }
  });

  // Add MutationObserver as fallback to detect DOM changes
  if (window.MutationObserver) {
    const cartObserver = new MutationObserver(function (mutations) {
      let shouldValidate = false;
      mutations.forEach(function (mutation) {
        if (mutation.type === "childList") {
          // Only trigger validation for actual cart item changes, not validation UI changes
          const hasCartItemChanges =
            Array.from(mutation.addedNodes).some(
              (node) =>
                node.nodeType === 1 &&
                (node.classList?.contains("cartItem") ||
                  node.querySelector?.(".cartItem")),
            ) ||
            Array.from(mutation.removedNodes).some(
              (node) =>
                node.nodeType === 1 &&
                (node.classList?.contains("cartItem") ||
                  node.querySelector?.(".cartItem")),
            );

          if (hasCartItemChanges) {
            shouldValidate = true;
          }
        }
      });

      if (shouldValidate && !cartValidationState.isValidating) {
        triggerValidation();
      }
    });

    // Observe the cart container for changes
    const cartContainer =
      document.getElementById("cartItems") || document.getElementById("cart");
    if (cartContainer) {
      cartObserver.observe(cartContainer, {
        childList: true,
        subtree: false, // Don't watch deep changes to avoid validation UI triggers
      });
    }
  }
}

// Auto-initialize if DOM is already loaded, otherwise wait for it
if (document.readyState === "loading") {
  initCartValidation();
} else {
  // DOM is already loaded - delay validation to ensure content is fully rendered
  if (document.querySelector(".cartItem")) {
    setTimeout(() => {
      validateCartItems();
    }, 500);
  }
  initCartValidation();
}
